import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  UsersIcon,
  MapIcon,
  TruckIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const MainLayout = () => {
  const { currentUser: user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Rol bazlı navigasyon menüsü
  const getNavigationByRole = (userRole) => {
    // Acente kullanıcısı menüsü
    if (userRole === 'agency_user') {
      return [
        { name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon },
        { name: t('navigation.reservations'), href: '/reservations', icon: CalendarIcon },
        { name: t('reservations.create'), href: '/reservations/create', icon: CalendarIcon },
        { name: t('navigation.transfers'), href: '/agency-transfers', icon: CalendarIcon },
      ];
    }

    // Taşıyıcı kullanıcısı menüsü
    if (userRole === 'carrier_user') {
      return [
        { name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon },
        { name: t('navigation.transfers'), href: '/carrier-transfers', icon: TruckIcon },
        { name: t('navigation.finance'), href: '/finance', icon: UserGroupIcon },
      ];
    }

    // Sistem kullanıcıları için tam menü
    return [
      { name: t('navigation.dashboard'), href: '/dashboard', icon: HomeIcon },
      { name: t('navigation.regions'), href: '/regions', icon: MapIcon },
      { name: t('navigation.agencies'), href: '/agencies', icon: UsersIcon },
      { name: t('navigation.carriers'), href: '/carriers', icon: TruckIcon },
      { name: t('navigation.vehicles'), href: '/vehicles', icon: TruckIcon },
      { name: 'Fiyatlar', href: '/prices', icon: CurrencyDollarIcon },
      { name: t('navigation.reservations'), href: '/reservations', icon: CalendarIcon },
      { name: t('navigation.operations'), href: '/operations', icon: TruckIcon },

      { name: t('navigation.finance'), href: '/finance', icon: CurrencyDollarIcon },
      { name: t('navigation.reports'), href: '/reports', icon: ChartBarIcon },
      { name: t('navigation.users'), href: '/user-management', icon: UserGroupIcon },
    ];
  };

  const navigation = getNavigationByRole(user?.role);

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 flex md:hidden ${sidebarOpen ? 'visible' : 'invisible'}`}>
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setSidebarOpen(false)}
        ></div>

        <div className={`relative flex-1 flex flex-col max-w-xs w-full bg-white transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center justify-between px-4">
              <h1 className="text-xl font-bold text-primary-600">CVR TOURISM & TRASNFER</h1>
              {/* Mobil için dil değiştirici - header'da */}
              <div className="ml-2">
                <LanguageSwitcher className="scale-90" />
              </div>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon
                    className={`mr-4 h-6 w-6 ${
                      isActive(item.href) ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex-shrink-0 group block">
              <div className="flex items-center">
                <div>
                  <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
                    {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-base font-medium text-gray-700">{user?.full_name || user?.username || 'Kullanıcı'}</p>
                  {user?.role === 'agency_user' && user?.agency && (
                    <p className="text-sm text-gray-500">{user.agency.name}</p>
                  )}
                  {user?.role === 'carrier_user' && user?.carrier && (
                    <p className="text-sm text-gray-500">{user.carrier.name}</p>
                  )}
                  <p className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ') || 'Kullanıcı'}</p>
                  <button
                    onClick={handleLogout}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 flex items-center"
                  >
                    <ArrowRightOnRectangleIcon className="mr-1 h-4 w-4" />
                    Çıkış Yap
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 w-14" aria-hidden="true">
          {/* Force sidebar to shrink to fit close icon */}
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ${
        sidebarCollapsed ? 'md:w-16' : 'md:w-64'
      }`}>
        <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
          {/* Header area with collapse button and language switcher */}
          <div className="flex items-center justify-between p-2 border-b border-gray-200">
            {/* Dil değiştirici - üst kısımda sol taraf */}
            {!sidebarCollapsed && (
              <div className="flex-1">
                <LanguageSwitcher className="scale-90" />
              </div>
            )}
            {sidebarCollapsed && (
              <div className="flex-1 flex justify-center">
                <LanguageSwitcher className="scale-75" />
              </div>
            )}
            
            {/* Collapse button */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              title={sidebarCollapsed ? 'Menüyü Genişlet' : 'Menüyü Daralt'}
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-5 w-5" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          <div className="flex-1 flex flex-col pb-4 overflow-y-auto">
            <div className={`flex items-center flex-shrink-0 px-4 py-4 ${sidebarCollapsed ? 'justify-center' : ''}`}>
              {sidebarCollapsed ? (
                <div className="text-xl font-bold text-primary-600">CVR</div>
              ) : (
                <h1 className="text-xl font-bold text-primary-600">CVR TOURISM & TRASNFER</h1>
              )}
            </div>
            <nav className="mt-2 flex-1 px-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <item.icon
                    className={`h-5 w-5 ${
                      isActive(item.href) ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    } ${sidebarCollapsed ? '' : 'mr-3'}`}
                    aria-hidden="true"
                  />
                  {!sidebarCollapsed && item.name}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex-shrink-0 w-full group block">
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <div>
                  <div className="h-9 w-9 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
                    {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                  </div>
                </div>
                {!sidebarCollapsed && (
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700">{user?.full_name || user?.username || 'Kullanıcı'}</p>
                    {user?.role === 'agency_user' && user?.agency && (
                      <p className="text-xs text-gray-500">{user.agency.name}</p>
                    )}
                    {user?.role === 'carrier_user' && user?.carrier && (
                      <p className="text-xs text-gray-500">{user.carrier.name}</p>
                    )}
                    <p className="text-xs text-gray-400 capitalize mb-1">{user?.role?.replace('_', ' ') || 'Kullanıcı'}</p>
                    {/* Dil değiştirici buradan kaldırıldı */}
                    <button
                      onClick={handleLogout}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center"
                    >
                      <ArrowRightOnRectangleIcon className="mr-1 h-4 w-4" />
                      {t('auth.logout')}
                    </button>
                  </div>
                )}
                {sidebarCollapsed && (
                  <button
                    onClick={handleLogout}
                    className="absolute left-1/2 transform -translate-x-1/2 mt-12 p-1 text-gray-500 hover:text-gray-700"
                    title={t('auth.logout')}
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${
        sidebarCollapsed ? 'md:pl-16' : 'md:pl-64'
      }`}>
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-white">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <main className="flex-1">
          <div className="py-6">
            <div className=" mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;